"use client"
import React from 'react';
import { motion } from 'framer-motion';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Instagram, Facebook, MapPin, Clock, Phone } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-b from-white to-neutral-50">
      {/* Main Footer */}
      <div className="border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
            {/* Brand & Social */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="lg:col-span-4 space-y-4"
            >
              <Link href="/">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10">
                    <Image
                      src="/logo/logo orange background embossed.jpg"
                      alt="Mochi Mochi Chillin"
                      fill
                      sizes="40px"
                      className="rounded-full object-cover"
                    />
                  </div>
                  <span className="font-heading text-lg font-bold bg-gradient-to-r from-primary-orange to-primary-light bg-clip-text text-transparent">
                    Mochi Mochi Chillin
                  </span>
                </div>
              </Link>
              <div className="flex items-center gap-4">
                <motion.a
                  href="https://www.instagram.com/mochi_mochi_chillin/"
                  className="p-2 rounded-full bg-neutral-100 text-primary-orange hover:bg-primary-orange hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Instagram className="w-4 h-4" />
                </motion.a>
                <motion.a
                  href="https://facebook.com/mochimochichillin"
                  className="p-2 rounded-full bg-neutral-100 text-primary-orange hover:bg-primary-orange hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Facebook className="w-4 h-4" />
                </motion.a>
                <span className="text-sm text-neutral-600 ml-1">Follow us for updates!</span>
              </div>
            </motion.div>

            {/* Contact & Hours */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="lg:col-span-4"
            >
              <div className="space-y-2.5">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-primary-orange shrink-0 mt-1" />
                  <a 
                    href="https://maps.google.com/?q=28910+Hardin+Store+Rd+suite+102,+Magnolia,+TX+77354"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-neutral-600 hover:text-primary-orange transition-colors duration-300"
                  >
                    28910 Hardin Store Rd suite 102, Magnolia, TX 77354
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-primary-orange shrink-0 mt-1" />
                  <div className="text-sm text-neutral-600">
                    Mon-Thu: 10AM–9PM • Fri-Sat: 10AM–10PM • Sun: 11AM–8PM
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary-orange shrink-0" />
                  <a href="tel:832-521-3125" className="text-sm text-neutral-600 hover:text-primary-orange transition-colors duration-300">
                    (832) 521-3125
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Quick Links & Newsletter */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="lg:col-span-4"
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {[
                    { name: 'Home', href: '/' },
                    { name: 'Menu', href: '/menu' },
                    { name: 'About', href: '/about' },
                    { name: 'Contact', href: '/contact' },
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' }
                  ].map((link) => (
                    <Link 
                      key={link.name}
                      href={link.href}
                      className="text-neutral-600 hover:text-primary-orange transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <form className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Subscribe to newsletter"
                    className="flex-1 h-9 text-sm"
                    required
                  />
                  <Button
                    type="submit"
                    className="h-9 px-3 bg-primary-orange hover:bg-primary-orange/90 text-white text-sm"
                  >
                    Join
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-neutral-200 py-4 text-center text-xs text-neutral-600">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          © {new Date().getFullYear()} Mochi Mochi Chillin. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;